import type { CharacterDefinition } from '../content/schema/character';
import { createGenericSkeletonRenderer } from './core/GenericSkeletonRenderer';
import { NEUTRAL_BIPED_DEFINITION } from './core/NeutralBiped';
import { createImportedSkeletonRenderer } from './rig/ImportedSkeletonRenderer';
import { getCharacterRenderer, registerCharacterRenderers } from './rendererRegistry';
import { registerRigBones } from './rigBonesRegistry';

/**
 * Register a project's own custom characters into the engine's character
 * registry: their rig's bone ids (so posing UI can list them) and, *only where
 * the host has not already registered a renderer of its own*, a renderer
 * bound to `definition.rendererId` so `getCharacterRenderer` resolves for
 * authoring and preview - a real skinned mesh (`createImportedSkeletonRenderer`)
 * when the definition carries an `importedGlbUrl` (SF22), otherwise the
 * generic per-bone gizmo fallback for flat bone-transform rigs.
 *
 * The conditional matters. Studio has no access to a game's renderer code and
 * genuinely needs the gizmo fallback, but a game mounting the same authoring UI
 * registers its real characters at startup and then loads the same roster -
 * unconditionally binding the fallback would replace the actual character with
 * labelled capsules, which is precisely the thing the shared studio exists to
 * avoid. The built-in neutral biped is already registered by the core pack and
 * is skipped here. Idempotent per rig.
 */
export function registerProjectCharacters(
  characters: CharacterDefinition[],
  rigBones: Record<string, string[]>,
): void {
  for (const definition of characters) {
    if (definition.id === NEUTRAL_BIPED_DEFINITION.id) continue;
    if (definition.importedGlbUrl) {
      if (!getCharacterRenderer(definition.rendererId)) {
        registerCharacterRenderers({ [definition.rendererId]: createImportedSkeletonRenderer(definition.importedGlbUrl) });
      }
      continue;
    }
    const bones = rigBones[definition.rigId];
    if (!bones || bones.length === 0) continue;
    registerRigBones(definition.rigId, bones);
    if (getCharacterRenderer(definition.rendererId)) continue;
    registerCharacterRenderers({ [definition.rendererId]: createGenericSkeletonRenderer(bones) });
  }
}
